import { useIonToast } from "@ionic/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SavedItinerariesApi } from "../services/SavedItinerariesApi";
import {
    authSlice,
    savedItinerariesSlice,
    type SavedItineraryEntry,
    usePersistedSliceSelector,
    usePersistenceReady,
} from "../store/persistence";
import { persistentSliceStore } from "../store/persistence/runtime/PersistentSliceStore";
import {
    buildSavedEntriesFromServer,
    findLocalOnlyExtras,
    keepLocalOnlyEntries,
    mergeServerAndLocalExtras,
    resolveSavedItinerariesAuthTrigger,
    toSavedItineraryUpsertDTO,
} from "../tools/savedItinerariesSync.tools";
import { SavedItinerariesMergePrompt } from "./SavedItinerariesMergePrompt";

/** Props for {@link SavedItinerariesBootstrap}. */
export interface SavedItinerariesBootstrapProps {

    /** Whether the dark UI palette is active. */
    "isDarkUi": boolean;
}

/**
 * Side-effect component syncing the saved-itinerary ("favorites") slice with
 * the session — the favorite counterpart of {@link PurchasedItinerariesBootstrap}.
 *
 * - every transition to a valid `accessToken` (boot with session, login, cookie
 *   refresh after expiry) downloads the profile favorites and merges them with
 *   the local store: local entries unknown to the profile are flagged
 *   `localOnly` — and surfaced through a merge prompt on real `login`
 *   transitions only (boot/refresh merges silently and the extras are offered
 *   again at the next login);
 * - every transition to `null` (logout, failed refresh, revoked session) purges
 *   profile-synced entries while `localOnly` ones stay on the device.
 *
 * A failed `GET` keeps the local list untouched — the store is never wiped on
 * a network error. Mounted once in `AppContent` (needs the IonApp context for
 * toasts and `isDarkUi` for the merge prompt).
 *
 * @param props - Component props.
 * @returns The merge prompt when local extras await a decision, else `null`.
 */
export const SavedItinerariesBootstrap: React.FC<SavedItinerariesBootstrapProps> = ( { isDarkUi } ) => {
    const persistenceReady = usePersistenceReady();
    const accessToken = usePersistedSliceSelector( authSlice, ( auth ) => auth.accessToken );
    const [ present ] = useIonToast();

    /** Previous token value; `undefined` until the first hydrated read. */
    const previousTokenRef = useRef<string | null | undefined>( undefined );

    /** Local extras awaiting a merge decision — `null` while no prompt is shown. */
    const [ mergeExtras, setMergeExtras ] = useState<SavedItineraryEntry[] | null>( null );

    useEffect( () => {
        if ( !persistenceReady ) {
            return;
        }

        const trigger = resolveSavedItinerariesAuthTrigger( previousTokenRef.current, accessToken );
        previousTokenRef.current = accessToken;

        if ( trigger === "bootAnonymous" || trigger === "unchanged" ) {
            return;
        }

        if ( trigger === "logout" ) {
            // Logout: drop profile-synced favorites, keep device-local ones.
            void ( async() => {
                try {
                    await persistentSliceStore.ensureHydrated( savedItinerariesSlice );
                    persistentSliceStore.setValue( savedItinerariesSlice, keepLocalOnlyEntries );
                } catch( error: unknown ) {
                    console.error( "[SavedItineraries] Failed to purge synced favorites on logout", error );
                }
            } )();

            return;
        }

        const tokenAtStart = accessToken;

        void ( async() => {
            try {
                // Lazy slice: hydrate before diffing so local extras are visible.
                await persistentSliceStore.ensureHydrated( savedItinerariesSlice );
                const serverEntries = await SavedItinerariesApi.getSavedItineraries();

                // Discard a stale response when the session changed mid-flight.
                if ( persistentSliceStore.peek( authSlice ).accessToken !== tokenAtStart ) {
                    return;
                }

                const localState = persistentSliceStore.peek( savedItinerariesSlice );
                const extras = findLocalOnlyExtras( localState, serverEntries );

                if ( extras.length === 0 ) {
                    persistentSliceStore.setValue( savedItinerariesSlice, buildSavedEntriesFromServer( serverEntries ) );

                    return;
                }

                /*
                 * Flag at detection (not only on refusal) so a logout before the
                 * decision never purges an entry the profile does not know.
                 */
                const flaggedExtras = extras.map( ( entry ) => ( {
                    ...entry,
                    "localOnly": true,
                } ) );
                persistentSliceStore.setValue(
                    savedItinerariesSlice,
                    mergeServerAndLocalExtras( serverEntries, flaggedExtras ),
                );

                /*
                 * The merge prompt is a login-transition affair: boot/refresh
                 * merges silently and the extras are offered again at the next
                 * real login.
                 */
                if ( trigger === "login" ) {
                    setMergeExtras( flaggedExtras );
                }
            } catch( error: unknown ) {
                // Membership unknown — keep the local list untouched.
                console.error( "[SavedItineraries] Failed to sync saved itineraries", error );
            }
        } )();
    }, [ accessToken, persistenceReady ] );

    /**
     * Pushes the pending extras into the profile and replaces the local store
     * with the authoritative server list. On failure the extras stay flagged
     * `localOnly` and are offered again at the next login.
     */
    const handleSyncToProfile = useCallback( async(): Promise<void> => {
        if ( mergeExtras === null ) {
            return;
        }

        const tokenAtStart = persistentSliceStore.peek( authSlice ).accessToken;

        try {
            // Push only extras still present locally (the user may have un-favorited some meanwhile).
            const localIds = new Set( persistentSliceStore.peek( savedItinerariesSlice ).map( ( entry ) => entry.itinerary.id ) );
            const extrasToPush = mergeExtras.filter( ( entry ) => localIds.has( entry.itinerary.id ) );

            if ( extrasToPush.length > 0 ) {
                const serverEntries = await SavedItinerariesApi.upsertSavedItineraries( extrasToPush.map( toSavedItineraryUpsertDTO ) );

                if ( persistentSliceStore.peek( authSlice ).accessToken !== tokenAtStart ) {
                    return;
                }

                persistentSliceStore.setValue( savedItinerariesSlice, buildSavedEntriesFromServer( serverEntries ) );
            }

            setMergeExtras( null );
        } catch( error: unknown ) {
            console.error( "[SavedItineraries] Failed to merge local favorites into the profile", error );

            // Keep the extras flagged `localOnly`; a retry will be proposed at the next login.
            setMergeExtras( null );
            void present( {
                "message":  "Impossible de synchroniser les favoris — ils restent enregistrés sur cet appareil.",
                "color":    "warning",
                "duration": 3500,
                "position": "bottom",
            } );
        }
    }, [ mergeExtras, present ] );

    /**
     * Defers the merge decision — extras stay flagged `localOnly` on this device.
     */
    const handleClose = useCallback( () => {
        setMergeExtras( null );
    }, [] );

    return (
        <SavedItinerariesMergePrompt
            isDarkUi={isDarkUi}
            isOpen={mergeExtras !== null}
            onClose={handleClose}
            onSyncToProfile={handleSyncToProfile}
        />
    );
};
